/**
 * Playbooks Page
 * Manage topic-focused knowledge playbooks loaded on-demand by the AI assistant
 */

import React, { useState, useEffect, useRef } from "react";
import {
  Plus,
  BookOpen,
  Search,
  Tag,
  Clock,
  User,
  Edit,
  Trash2,
  Upload,
  Globe,
  Lock,
  Library,
} from "lucide-react";
import axios from "axios";
import apiService from "../services/api";

interface Playbook {
  id: number;
  title: string;
  description: string;
  category: string;
  tags: string[];
  is_public: boolean;
  is_system: boolean;
  created_by_username: string;
  created_at: string;
  updated_at: string;
  version: number;
}

export const Playbooks: React.FC = () => {
  // State
  const [playbooks, setPlaybooks] = useState<Playbook[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [currentUsername, setCurrentUsername] = useState<string | null>(null);

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Categories for filtering
  const categories = [
    "code_generation",
    "document_processing",
    "data_transformation",
    "api_integration",
    "testing",
    "deployment",
    "other",
  ];

  // Fetch current user
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const user = await apiService.getCurrentUser();
        setCurrentUsername(user.username);
      } catch (err) {
        console.error("Failed to fetch current user:", err);
      }
    };
    fetchCurrentUser();
  }, []);

  // Fetch playbooks
  const fetchPlaybooks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("auth_token");
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

      const params = new URLSearchParams();
      if (selectedCategory) params.append("category", selectedCategory);
      if (searchTerm) params.append("search", searchTerm);

      const response = await axios.get(
        `${apiUrl}/api/playbooks?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setPlaybooks(response.data.playbooks || []);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch playbooks:", err);
      setError("Failed to load playbooks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlaybooks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, searchTerm]);

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to archive this playbook?")) {
      return;
    }

    try {
      const token = localStorage.getItem("auth_token");
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

      await axios.post(
        `${apiUrl}/api/playbooks/${id}/archive`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // Refresh list
      fetchPlaybooks();
    } catch (err) {
      console.error("Failed to archive playbook:", err);
      alert("Failed to archive playbook");
    }
  };

  const handleEdit = (id: number) => {
    // For now, just view the playbook details
    // TODO: Create edit page
    window.location.href = `/playbooks/${id}`;
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset input so same file can be selected again
    event.target.value = "";

    try {
      setImporting(true);
      const result = await apiService.importPlaybook(file);

      alert(`Playbook imported successfully: ${result.title}`);

      // Refresh list and navigate to new playbook
      await fetchPlaybooks();
      window.location.href = `/playbooks/${result.id}`;
    } catch (err: any) {
      console.error("Failed to import playbook:", err);
      alert(err.response?.data?.detail || "Failed to import playbook");
    } finally {
      setImporting(false);
    }
  };

  // Filter playbooks
  const filteredPlaybooks = playbooks;

  // Helper to check if playbook is owned by current user
  const isOwnedByCurrentUser = (playbook: Playbook): boolean => {
    return (
      currentUsername !== null &&
      playbook.created_by_username === currentUsername
    );
  };

  // Helper to render visibility badge
  const renderVisibilityBadge = (playbook: Playbook) => {
    if (playbook.is_system) {
      return (
        <div className="flex items-center gap-1 px-2 py-1 bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 rounded text-xs font-medium">
          <Library className="h-3 w-3" />
          <span>System</span>
        </div>
      );
    } else if (playbook.is_public) {
      return (
        <div className="flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded text-xs font-medium">
          <Globe className="h-3 w-3" />
          <span>Public</span>
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded text-xs font-medium">
          <Lock className="h-3 w-3" />
          <span>Private</span>
        </div>
      );
    }
  };

  // Helper to get border classes based on visibility
  const getVisibilityBorderClass = (playbook: Playbook): string => {
    if (playbook.is_system) {
      return "border-purple-300 dark:border-purple-700";
    } else if (playbook.is_public) {
      return "border-green-300 dark:border-green-700";
    } else {
      return "border-blue-300 dark:border-blue-700";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BookOpen className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Playbooks
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Topic-focused knowledge loaded on-demand by the AI assistant
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleFileChange}
                className="hidden"
              />
              <button
                onClick={handleImportClick}
                disabled={importing}
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
              >
                <Upload className="h-5 w-5" />
                {importing ? "Importing..." : "Import"}
              </button>
              <button
                onClick={() => (window.location.href = "/playbooks/new")}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                <Plus className="h-5 w-5" />
                New Playbook
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search playbooks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Category Filter */}
          <select
            value={selectedCategory || ""}
            onChange={(e) => setSelectedCategory(e.target.value || null)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-300 dark:border-gray-600 border-t-blue-600"></div>
            <p className="mt-4 text-gray-500 dark:text-gray-400">
              Loading playbooks...
            </p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600 dark:text-red-400">{error}</p>
            <button
              onClick={fetchPlaybooks}
              className="mt-4 text-blue-600 hover:text-blue-700 dark:text-blue-400"
            >
              Try again
            </button>
          </div>
        ) : filteredPlaybooks.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              No playbooks found
            </p>
            <button
              onClick={() => (window.location.href = "/playbooks/new")}
              className="mt-4 text-blue-600 hover:text-blue-700 dark:text-blue-400"
            >
              Create your first playbook
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPlaybooks.map((playbook) => {
              const isOwned = isOwnedByCurrentUser(playbook);
              const visibilityBorderClass = getVisibilityBorderClass(playbook);
              const ownershipBorderClass = isOwned
                ? "border-l-4 border-l-blue-500"
                : "";

              return (
                <div
                  key={playbook.id}
                  className={`bg-white dark:bg-gray-800 rounded-lg border p-6 hover:shadow-lg transition-shadow relative ${visibilityBorderClass} ${ownershipBorderClass}`}
                >
                  {/* Visibility Badge - Top Right */}
                  <div className="absolute top-4 right-4">
                    {renderVisibilityBadge(playbook)}
                  </div>

                  {/* Header */}
                  <div className="flex items-start justify-between mb-3 pr-20">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex-1">
                      {playbook.title}
                    </h3>
                    <div className="flex items-center gap-2 ml-2">
                      <button
                        onClick={() => handleEdit(playbook.id)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                        title="View"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(playbook.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        title="Archive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
                    {playbook.description}
                  </p>

                  {/* Category */}
                  <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-3">
                    <Tag className="h-4 w-4" />
                    <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded">
                      {playbook.category.replace("_", " ")}
                    </span>
                  </div>

                  {/* Tags */}
                  {playbook.tags && playbook.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {playbook.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded"
                        >
                          {tag}
                        </span>
                      ))}
                      {playbook.tags.length > 3 && (
                        <span className="px-2 py-1 text-xs text-gray-500 dark:text-gray-400">
                          +{playbook.tags.length - 3} more
                        </span>
                      )}
                    </div>
                  )}

                  {/* Footer */}
                  <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      <span
                        className={
                          isOwned
                            ? "font-semibold text-blue-600 dark:text-blue-400"
                            : ""
                        }
                      >
                        {isOwned ? "You" : playbook.created_by_username}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span>v{playbook.version}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
